import classNames from "classnames";
import React, { FC, KeyboardEvent, useMemo, useState } from "react";
import { LuInfo, LuMail, LuPaperclip, LuSend, LuX } from "react-icons/lu";
import { toast } from "react-toastify";

import { useSendInvoiceNoReplyMutation } from "../../../../services/BillingService";
import {
  errorText,
  modalCancel,
  modalInput,
  modalPrimary,
  PortalModal,
} from "../../../clientPortal/PortalModal";
import {
  addUnique,
  base64Bytes,
  buildNoReplyPayload,
  defaultInvoiceMessage,
  defaultInvoiceSubject,
  firmDisplayName,
  FirmForEmail,
  InvoiceForEmail,
  invoiceRecipients,
  isValidEmail,
  MAX_MESSAGE,
  MAX_PDF_BYTES,
  MAX_RECIPIENTS,
  MAX_SUBJECT,
  noReplyFooter,
  noReplyProblem,
  SendMethod,
  sentToast,
  splitRecipientInput,
} from "./sendInvoice.utils";

type Props = {
  invoiceId: string;
  invoice?: InvoiceForEmail | null;
  firm: FirmForEmail;
  fileName: string;
  /** The person's connected mailbox (Nylas), or "" when none. */
  mailboxEmail: string;
  getPdfBase64: () => Promise<string>;
  /** "Send from my mailbox": attach the PDF and open the usual compose window. */
  onUseMailbox: () => Promise<void> | void;
  onClose: () => void;
};

/**
 * Send Invoice (Roadmap 1789): send from ProCharted's no-reply address (default,
 * through SendGrid) or from the person's own mailbox (the existing compose window).
 */
export const SendInvoiceModal: FC<Props> = ({
  invoiceId,
  invoice,
  firm,
  fileName,
  mailboxEmail,
  getPdfBase64,
  onUseMailbox,
  onClose,
}) => {
  const firmName = firmDisplayName(firm);
  const [method, setMethod] = useState<SendMethod>("noReply");
  const [to, setTo] = useState<string[]>(() => invoiceRecipients(invoice));
  const [pending, setPending] = useState("");
  const [subject, setSubject] = useState(() =>
    defaultInvoiceSubject(invoice, firmName),
  );
  const [message, setMessage] = useState(() =>
    defaultInvoiceMessage(invoice, firmName),
  );
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [send] = useSendInvoiceNoReplyMutation();

  const footer = useMemo(() => noReplyFooter(firmName), [firmName]);

  const commitPending = () => {
    const values = splitRecipientInput(pending);
    if (values.length === 0) return;
    setTo((prev) => addUnique(prev, values));
    setPending("");
    setProblem(null);
  };

  const onRecipientKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === ";" || e.key === " ") {
      if (pending.trim()) {
        e.preventDefault();
        commitPending();
      }
    } else if (e.key === "Backspace" && !pending && to.length > 0) {
      setTo((prev) => prev.slice(0, -1));
    }
  };

  const submit = async () => {
    if (method === "mailbox") {
      setBusy(true);
      try {
        await onUseMailbox();
      } finally {
        setBusy(false);
      }
      return;
    }
    const found = noReplyProblem({
      to,
      subject,
      message,
      pendingInput: pending,
    });
    if (found) return setProblem(found);
    setBusy(true);
    setProblem(null);
    try {
      const pdfBase64 = await getPdfBase64();
      if (base64Bytes(pdfBase64) > MAX_PDF_BYTES) {
        setProblem("The invoice PDF is larger than 10 MB.");
        return;
      }
      const payload = buildNoReplyPayload({
        to,
        pendingInput: pending,
        subject,
        message,
        pdfBase64,
        fileName,
      });
      const result = await send({ invoiceId, ...payload });
      if ("error" in result) {
        setProblem(errorText(result.error, "The invoice couldn't be sent."));
        return;
      }
      toast(sentToast(result.data.to), { type: "success" });
      onClose();
    } catch {
      setProblem("The invoice PDF couldn't be prepared. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const option = (
    value: SendMethod,
    title: string,
    text: string,
    disabled = false,
    tag?: string,
  ) => (
    <label
      className={classNames(
        "flex cursor-pointer items-start gap-3 rounded-[12px] border-[1.5px] px-3.5 py-3",
        method === value
          ? "border-[#6B4EFF] bg-[#F6F4FF]"
          : "border-[#DCE2EA] bg-white hover:bg-[#FAFAFC]",
        disabled && "cursor-not-allowed opacity-60",
      )}>
      <input
        type={"radio"}
        name={"send-invoice-method"}
        className={"mt-1 accent-[#6B4EFF]"}
        checked={method === value}
        disabled={disabled}
        onChange={() => {
          setMethod(value);
          setProblem(null);
        }}
      />
      <span className={"flex min-w-0 flex-col gap-0.5"}>
        <span
          className={
            "flex items-center gap-2 text-[13.5px] font-extrabold text-[#1F2433]"
          }>
          {title}
          {tag && (
            <span
              className={
                "rounded-full bg-[#EBE6FD] px-2 py-0.5 text-[11px] font-bold text-[#6938EF]"
              }>
              {tag}
            </span>
          )}
        </span>
        <span className={"text-[12.5px] font-medium text-[#6B7185]"}>
          {text}
        </span>
      </span>
    </label>
  );

  const label = "text-[12.5px] font-bold text-[#3A4256]";

  return (
    <PortalModal
      title={`Send invoice #${invoice?.invoiceNo ?? ""}`}
      subtitle={"Choose how the email is sent."}
      onClose={onClose}
      width={600}
      footer={
        <>
          {problem && (
            <span
              role={"alert"}
              className={"mr-auto text-[12.5px] font-semibold text-[#D92D20]"}>
              {problem}
            </span>
          )}
          <button type={"button"} className={modalCancel} onClick={onClose}>
            Cancel
          </button>
          <button
            type={"button"}
            className={classNames(
              modalPrimary,
              "inline-flex items-center gap-2",
            )}
            disabled={busy}
            onClick={submit}>
            {method === "noReply" ? <LuSend size={15} /> : <LuMail size={15} />}
            {method === "noReply"
              ? busy
                ? "Sending…"
                : "Send invoice"
              : busy
              ? "Opening…"
              : "Open email window"}
          </button>
        </>
      }>
      <div className={"flex flex-col gap-2.5"}>
        {option(
          "noReply",
          "Send from ProCharted (no-reply)",
          `Sent by ProCharted for ${firmName}, with the invoice PDF attached. The client can't reply to it.`,
          false,
          "Default",
        )}
        {option(
          "mailbox",
          "Send from my mailbox",
          mailboxEmail
            ? `Opens the email window with your connected mailbox (${mailboxEmail}). Replies come to you.`
            : "Connect your mailbox first: Mail page on the left menu, then click “Connect”.",
          !mailboxEmail,
        )}
      </div>

      {method === "noReply" && (
        <>
          <div className={"flex flex-col gap-1.5"}>
            <span className={label}>To</span>
            <div
              className={
                "min-h-10 flex w-full flex-wrap items-center gap-1.5 rounded-[9px] border-[1.5px] border-[#DCE2EA] px-2 py-1.5 focus-within:border-[#6B4EFF]"
              }>
              {to.map((email) => (
                <span
                  key={email}
                  className={classNames(
                    "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12.5px] font-semibold",
                    isValidEmail(email)
                      ? "bg-[#F1F2F5] text-[#3A4256]"
                      : "bg-[#FEF3F2] text-[#D92D20]",
                  )}>
                  {email}
                  <button
                    type={"button"}
                    aria-label={`Remove ${email}`}
                    className={"text-[#9097A8] hover:text-[#1F2433]"}
                    onClick={() =>
                      setTo((prev) => prev.filter((x) => x !== email))
                    }>
                    <LuX size={13} />
                  </button>
                </span>
              ))}
              <input
                aria-label={"Add an email address"}
                className={
                  "h-7 min-w-[160px] flex-1 border-none text-[13.5px] outline-none placeholder:text-[#9097A8]"
                }
                placeholder={
                  to.length === 0
                    ? "Type an email address and press Enter"
                    : to.length < MAX_RECIPIENTS
                    ? "Add another"
                    : ""
                }
                value={pending}
                onChange={(e) => setPending(e.target.value)}
                onKeyDown={onRecipientKey}
                onBlur={commitPending}
              />
            </div>
            {to.length === 0 && !pending && (
              <span className={"text-[12px] font-medium text-[#8A5A00]"}>
                This client has no email address on file. Type one above.
              </span>
            )}
          </div>

          <label className={"flex flex-col gap-1.5"}>
            <span className={label}>Subject</span>
            <input
              className={modalInput}
              maxLength={MAX_SUBJECT}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </label>

          <label className={"flex flex-col gap-1.5"}>
            <span className={label}>Message</span>
            <textarea
              className={classNames(modalInput, "h-32 resize-y py-2 leading-5")}
              maxLength={MAX_MESSAGE}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </label>

          <div
            className={
              "flex items-center gap-2 self-start rounded-[9px] border border-[#EDEFF3] bg-[#FBFBFD] px-3 py-2 text-[12.5px] font-semibold text-[#3A4256]"
            }>
            <LuPaperclip size={14} className={"text-[#6B4EFF]"} />
            {fileName}
            <span className={"font-medium text-[#9097A8]"}>PDF, attached</span>
          </div>

          <div
            className={
              "flex items-start gap-2 rounded-[10px] bg-[#F4F5F7] px-3 py-2.5 text-[12px] font-medium text-[#6B7185]"
            }>
            <LuInfo size={14} className={"mt-0.5 shrink-0"} />
            <span>
              <span className={"font-bold text-[#3A4256]"}>
                Added at the end of the email:
              </span>{" "}
              {footer}
            </span>
          </div>
        </>
      )}
    </PortalModal>
  );
};
